import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../assets/styles/search.box.css';
import { FaX } from 'react-icons/fa6';
import { useGetSuggestionsQuery } from '../../slices/productApiSlice';
import {
    useGetSearchHistoryQuery,
    useAddSearchTermMutation,
    useClearSearchHistoryMutation,
} from '../../slices/searchHistoryApiSlice';

const SearchBox = ({ autoFocus = false, onFocusChange, onKeywordChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { keyword: urlKeyword } = useParams();
    const [keyword, setKeyword] = useState(urlKeyword || '');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const searchRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    const isAdminProductListRoute = location.pathname.includes('/admin/productlist');
    const isAdminOutOfStockRoute = location.pathname.includes('/admin/outofstock');
    // Product suggestions jump to the storefront product page. The admin
    // product list uses this field as a filter, so that autocomplete stays off.
    const suggestionsEnabled = !isAdminProductListRoute;

    const { data: suggestions, isLoading } = useGetSuggestionsQuery(
        { keyword },
        { skip: !suggestionsEnabled || keyword.trim().length < 2 }
    );
    const {
        data: searchHistory = [],
        refetch: refetchHistory
    } = useGetSearchHistoryQuery();
    const [addSearchTerm] = useAddSearchTermMutation();
    const [clearSearchHistory, { isLoading: isClearing }] = useClearSearchHistoryMutation();

    const resolveAdminSearchPath = (term) => {
        if (isAdminOutOfStockRoute) {
            return term ? `/admin/outofstock/search/${term}` : '/admin/outofstock';
        }

        if (isAdminProductListRoute) {
            return term ? `/admin/productlist/search/${term}` : '/admin/productlist';
        }

        return term ? `/search/${term}` : '/';
    };

    useEffect(() => {
        const nextKeyword = urlKeyword || '';
        setKeyword(nextKeyword);
        if (onKeywordChange) onKeywordChange(nextKeyword);
    }, [urlKeyword, onKeywordChange]);

    const closePanels = useCallback(() => {
        setShowSuggestions(false);
        setIsInputFocused(false);
        if (onFocusChange) onFocusChange(false);
    }, [onFocusChange]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (searchRef.current && searchRef.current.contains(event.target)) {
                return;
            }
            closePanels();
            if (document.activeElement === inputRef.current) {
                inputRef.current.blur();
            }
        };

        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            closePanels();
            inputRef.current?.blur();
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown);
            if (blurTimeoutRef.current) {
                window.clearTimeout(blurTimeoutRef.current);
            }
        };
    }, [closePanels]);

    const setKeywordState = (nextKeyword) => {
        setKeyword(nextKeyword);
        if (onKeywordChange) onKeywordChange(nextKeyword);
    };

    const clearKeyword = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (blurTimeoutRef.current) {
            window.clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        // The clear control only erases the query. Search stays open and focused
        // so the shopper can type the next term without reopening the field.
        setKeywordState('');
        setShowSuggestions(false);
        setIsInputFocused(true);
        if (onFocusChange) onFocusChange(true);
        inputRef.current?.focus({ preventScroll: true });
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        closePanels();
        if (keyword.trim()) {
            const trim = keyword.trim();
            try {
                await addSearchTerm(trim);
                refetchHistory();
            } catch {}
            navigate(resolveAdminSearchPath(trim));
        } else {
            navigate(resolveAdminSearchPath(''));
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setKeywordState(suggestion.name);
        closePanels();
        navigate(`/product/${suggestion._id}`);
    };

    // Show history if input is focused and empty or <2 chars
    const showHistory = isInputFocused && (!keyword.trim() || keyword.trim().length < 2) && searchHistory.length > 0;

    return (
        <div className="search-box-wrapper w-100 position-relative" ref={searchRef}>
            <Form onSubmit={submitHandler} className="w-100" autoComplete="off">
                <InputGroup className="w-100 shadow-sm rounded-pill overflow-hidden border border-secondary-subtle">
                    <Form.Control
                        placeholder="¿Qué estás buscando hoy?"
                        aria-label="Buscar Productos"
                        aria-autocomplete={suggestionsEnabled ? 'list' : 'none'}
                        type="text"
                        name="q"
                        className="border-0 py-2 ps-4"
                        onChange={(e) => {
                            const val = e.target.value;
                            setKeywordState(val);
                            setShowSuggestions(suggestionsEnabled && val.trim().length >= 2);
                        }}
                        onFocus={() => {
                            setIsInputFocused(true);
                            if (suggestionsEnabled && keyword.trim().length >= 2) {
                                setShowSuggestions(true);
                            }
                            if (onFocusChange) onFocusChange(true);
                        }}
                        onBlur={(event) => {
                            const nextTarget = event.relatedTarget;
                            if (nextTarget && searchRef.current?.contains(nextTarget)) {
                                return;
                            }
                            if (blurTimeoutRef.current) {
                                window.clearTimeout(blurTimeoutRef.current);
                            }
                            blurTimeoutRef.current = window.setTimeout(() => {
                                if (searchRef.current?.contains(document.activeElement)) {
                                    return;
                                }
                                closePanels();
                            }, 150);
                        }}
                        value={keyword}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        style={{ boxShadow: 'none' }}
                        ref={inputRef}
                    />
                    
                    {isLoading && (
                        <div className="d-flex align-items-center bg-white px-2">
                            <Spinner animation="border" size="sm" style={{ color: '#b5430f' }} />
                        </div>
                    )}

                    {keyword && (
                        <Button
                            type="button"
                            variant="link"
                            className="bg-white border-0 text-muted d-flex align-items-center px-3"
                            onMouseDown={(event) => event.preventDefault()}
                            onPointerDown={(event) => event.preventDefault()}
                            onClick={clearKeyword}
                            style={{ textDecoration: 'none' }}
                            title="Limpiar búsqueda"
                            aria-label="Limpiar búsqueda"
                        >
                            <FaX size={14} />
                        </Button>
                    )}
                    
                    <Button 
                        variant="primary" 
                        type="submit"
                        className="px-4 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                    >
                        <FaSearch />
                    </Button>
                </InputGroup>
            </Form>

            {showHistory && (
                <div className="suggestions-container shadow-lg rounded-4 mt-2">
                    <div className="d-flex justify-content-between align-items-center px-3 pt-2 pb-1">
                        <span className="fw-bold" style={{ color: '#b5430f' }}>Historial de búsqueda</span>
                        <button
                            className="btn btn-link p-0 text-danger"
                            style={{ color: '#b5430f', fontSize: 13, textDecoration: 'underline' }}
                            type="button"
                            onPointerDown={async (event) => {
                                event.preventDefault();
                                await clearSearchHistory();
                                refetchHistory();
                            }}
                            disabled={isClearing}
                        >
                            Limpiar
                        </button>
                    </div>
                    <ul className="suggestions-list m-0 p-0">
                        {searchHistory.map((item) => (
                            <li
                                key={item.searchedAt + item.term}
                                className="suggestion-item d-flex align-items-center p-3 border-bottom"
                                style={{ cursor: 'pointer' }}
                                onPointerDown={async (event) => {
                                    event.preventDefault();
                                    setKeywordState(item.term);
                                    closePanels();
                                    try {
                                        await addSearchTerm(item.term);
                                        refetchHistory();
                                    } catch {}
                                    navigate(resolveAdminSearchPath(item.term));
                                }}
                            >
                                <span className="flex-grow-1 text-dark">{item.term}</span>
                                <small className="text-muted ms-2">{new Date(item.searchedAt).toLocaleDateString('es-EC', { hour: '2-digit', minute: '2-digit' })}</small>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {suggestionsEnabled && showSuggestions && suggestions && suggestions.length > 0 && !showHistory && (
                <div className="suggestions-container shadow-lg rounded-4 mt-2">
                    <ul className="suggestions-list m-0 p-0">
                        {suggestions.map((suggestion) => (
                            <li
                                key={suggestion._id}
                                className="suggestion-item d-flex align-items-center p-3 border-bottom"
                                onPointerDown={(event) => {
                                    event.preventDefault();
                                    handleSuggestionClick(suggestion);
                                }}
                            >
                                <div className="suggestion-image-wrapper me-3">
                                    <img
                                        src={suggestion.image || '/images/default.jpg'}
                                        alt={suggestion.name}
                                        className="suggestion-image rounded-3"
                                        onError={(e) => {
                                            e.target.src = '/images/default.jpg';
                                        }}
                                    />
                                </div>
                                <div className="suggestion-info flex-grow-1">
                                    <span className="suggestion-name fw-semibold text-dark d-block">
                                        {suggestion.name}
                                    </span>
                                    <small className="text-muted">Ver producto</small>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SearchBox;
